import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { createObjective, updateMilestoneMap, updateObjective, refreshMyMilestoneMaps, refreshSharedMilestoneMaps } from '../../store/map3PendingSlice';
import { removePlaceholderRow } from '../../store/map3UISlice';
import { Objective1 } from '../../types';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getAccessibleMaps } from '../../utils/getAccessibleMapsUtil';
import { fetchMilestoneMap, fetchObjectives } from '../../store/map3DataSlice';
import { calculateObjectiveRelationshipUpdates } from '@mm-shared/hierarchy';

interface ObjectivePlaceholderInputProps {
  row: number;
  milestoneMapUuid: string;
  parentUuid?: string;
  handleKeyDown?: (e: React.KeyboardEvent, row: number, col: number) => void;
  handleCellFocus?: (row: number, col: number) => void;
}

function AddNewObjectiveInput({
  row,
  milestoneMapUuid,
  parentUuid,
  handleKeyDown: gridHandleKeyDown,
  handleCellFocus: gridHandleCellFocus,
}: ObjectivePlaceholderInputProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [text, setText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingType, setEditingType] = useState<'parent' | 'child' | null>(null);
  const [internalFocus, setInternalFocus] = useState<'parent' | 'child' | null>('parent');
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === milestoneMapUuid),
  );
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);

  const handleSubmit = (text: string, type: 'parent' | 'child') => {
    if (!text.trim() || !milestoneMap) {
      setIsEditing(false);
      setEditingType(null);
      return;
    }

    const now = new Date().toISOString();
    const newUuid = crypto.randomUUID();

    const newObjective: Objective1 = {
      uuid: newUuid,
      shortId: getShortIdFromUuid(newUuid),
      text: text.trim(),
      parentIds: [],
      childIds: [],
      createdDate: now,
      createdBy: 'user',
      statuses: [],
      results: [],
      comments: [],
      ownerName: userProfile.userName,
      ownerId: userProfile.uid,
    };

    try {
      // Dispatch synchronously for optimistic updates
      dispatch(createObjective(newObjective));

      // Insert the new objective UUID into the correct position
      const updatedObjectiveUuids = [...milestoneMap.objectiveUuids];
      updatedObjectiveUuids.splice(row, 0, newUuid);

      // Create updates object with base properties
      const updates: any = {
        objectiveUuids: updatedObjectiveUuids,
      };

      // If this is a parent objective, also update parentObjectiveUuids
      if (type === 'parent') {
        const updatedParentUuids = [...(milestoneMap.parentObjectiveUuids || [])];
        updatedParentUuids.push(newUuid);
        updates.parentObjectiveUuids = updatedParentUuids;
      }

      // Update the milestone map with the new objective and parent designation
      dispatch(
        updateMilestoneMap({
          uuid: milestoneMapUuid,
          updates,
        }),
      );

      // Calculate and apply parent/child relationship updates based on the new structure
      const allObjectivesIncludingNew = [...allObjectives, newObjective];
      const updatedMilestoneMap = { ...milestoneMap, ...updates };
      const relationshipUpdates = calculateObjectiveRelationshipUpdates(
        updatedMilestoneMap,
        allObjectivesIncludingNew,
      );

      // Apply the calculated updates to each objective that needs changes
      Object.entries(relationshipUpdates).forEach(([objectiveUuid, updates]) => {
        dispatch(updateObjective({ uuid: objectiveUuid, updates }));
      });

      // Remove the placeholder row
      dispatch(removePlaceholderRow({ row }));
    } catch (error) {
      console.error('Failed to create objective:', error);
    }
    setIsEditing(false);
    setEditingType(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, type: 'parent' | 'child') => {
    // Don't intercept arrow keys with modifiers (Command/Ctrl+Arrow) - let grid handle them
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') && 
        (e.metaKey || e.ctrlKey)) {
      if (gridHandleKeyDown) {
        gridHandleKeyDown(e, row, 0);
      }
      return;
    }

    // Handle Tab and arrow right for internal navigation
    if (e.key === 'Tab' || e.key === 'ArrowRight') {
      e.stopPropagation();
      e.preventDefault();
      
      if (type === 'parent') {
        // Move from parent button to child area
        setInternalFocus('child');
        // Focus the child button
        const childButton = e.currentTarget.parentElement?.querySelector('[data-testid="add-child-button"]') as HTMLElement;
        if (childButton) {
          childButton.focus();
        }
      } else {
        // Move from child area to next grid cell
        setInternalFocus('parent');
        if (gridHandleKeyDown) {
          gridHandleKeyDown(e, row, 0);
        }
      }
      return;
    }
    
    // Handle arrow up/down for grid navigation
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Let the grid handle arrow key navigation
      if (gridHandleKeyDown) {
        gridHandleKeyDown(e, row, 0);
      }
      return;
    }
    
    // Handle arrow left
    if (e.key === 'ArrowLeft') {
      if (type === 'child') {
        // Move from child area to parent button
        e.stopPropagation();
        e.preventDefault();
        setInternalFocus('parent');
        const parentButton = e.currentTarget.parentElement?.querySelector('[data-testid="add-parent-button"]') as HTMLElement;
        if (parentButton) {
          parentButton.focus();
        }
      } else {
        // Let grid handle left arrow from parent button
        if (gridHandleKeyDown) {
          gridHandleKeyDown(e, row, 0);
        }
      }
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      // Stop propagation only for keys we handle to prevent grid navigation conflicts
      e.stopPropagation();
      e.preventDefault();
      setIsEditing(true);
      setEditingType(type);
      setText('');
    }
    // Allow other navigation keys to bubble up to grid navigation
  };

  if (isEditing) {
    return (
      <div className="flex w-full">
        {editingType === 'parent' && (
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => handleSubmit(text, 'parent')}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                handleSubmit(text, 'parent');
              } else if (e.key === 'Escape') {
                setIsEditing(false);
                setEditingType(null);
              }
            }}
            className="w-full px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white shadow-lg"
            placeholder="Enter parent objective..."
            autoFocus
          />
        )}
        {editingType === 'child' && (
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => handleSubmit(text, 'child')}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                handleSubmit(text, 'child');
              } else if (e.key === 'Escape') {
                setIsEditing(false);
                setEditingType(null);
              }
            }}
            className="w-full px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white shadow-lg"
            placeholder="Enter child objective..."
            autoFocus
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full">
      <div
        className="w-8 px-2 py-1 text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700 flex items-center justify-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
        onClick={() => {
          setIsEditing(true);
          setEditingType('parent');
          setText('');
        }}
        onKeyDown={(e) => handleKeyDown(e, 'parent')}
        role="button"
        aria-label="Add parent objective"
        data-testid="add-parent-button"
        tabIndex={0}
      >
        <span className="text-lg">+</span>
      </div>
      <div
        className="flex-1 px-2 py-1 text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        onClick={() => {
          setIsEditing(true);
          setEditingType('child');
          setText('');
        }}
        onKeyDown={(e) => handleKeyDown(e, 'child')}
        role="button"
        aria-label="Add child objective"
        data-testid="add-child-button"
        tabIndex={internalFocus === 'child' ? 0 : -1}
      >
        Click to add new objective...
      </div>
    </div>
  );
}

function AddExistingObjectiveInput({
  row,
  milestoneMapUuid,
  parentUuid,
  handleKeyDown: gridHandleKeyDown,
  handleCellFocus: gridHandleCellFocus,
}: ObjectivePlaceholderInputProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [isEditing, setIsEditing] = useState(false);
  const [editingType, setEditingType] = useState<'parent' | 'child' | null>(null);
  const [selectedMapUuid, setSelectedMapUuid] = useState<string>('');
  const [mapObjectives, setMapObjectives] = useState<Objective1[]>([]);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [internalFocus, setInternalFocus] = useState<'parent' | 'child' | null>('parent');

  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === milestoneMapUuid),
  );
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  const accessibleMaps = getAccessibleMaps(userProfile);

  // Refresh maps on mount so the accessible maps list is up to date
  useEffect(() => {
    dispatch(refreshMyMilestoneMaps());
    dispatch(refreshSharedMilestoneMaps());
  }, [dispatch]);

  const handleKeyDown = (e: React.KeyboardEvent, type: 'parent' | 'child') => {
    // Don't intercept arrow keys with modifiers (Command/Ctrl+Arrow) - let grid handle them
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') && 
        (e.metaKey || e.ctrlKey)) {
      if (gridHandleKeyDown) {
        gridHandleKeyDown(e, row, 0);
      }
      return;
    }

    // Handle Tab and arrow right for internal navigation
    if (e.key === 'Tab' || e.key === 'ArrowRight') {
      e.stopPropagation();
      e.preventDefault();
      
      if (type === 'parent') {
        // Move from parent button to child area
        setInternalFocus('child');
        // Focus the child button
        const childButton = e.currentTarget.parentElement?.querySelector('[data-testid="add-child-button"]') as HTMLElement;
        if (childButton) {
          childButton.focus();
        }
      } else {
        // Move from child area to next grid cell
        setInternalFocus('parent');
        if (gridHandleKeyDown) {
          gridHandleKeyDown(e, row, 0);
        }
      }
      return;
    }
    
    // Handle arrow up/down for grid navigation
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Let the grid handle arrow key navigation
      if (gridHandleKeyDown) {
        gridHandleKeyDown(e, row, 0);
      }
      return;
    }
    
    // Handle arrow left
    if (e.key === 'ArrowLeft') {
      if (type === 'child') {
        // Move from child area to parent button
        e.stopPropagation();
        e.preventDefault();
        setInternalFocus('parent');
        const parentButton = e.currentTarget.parentElement?.querySelector('[data-testid="add-parent-button"]') as HTMLElement;
        if (parentButton) {
          parentButton.focus();
        }
      } else {
        // Let grid handle left arrow from parent button
        if (gridHandleKeyDown) {
          gridHandleKeyDown(e, row, 0);
        }
      }
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      // Stop propagation only for keys we handle to prevent grid navigation conflicts
      e.stopPropagation();
      e.preventDefault();
      setIsEditing(true);
      setEditingType(type);
    }
    // Allow other navigation keys to bubble up to grid navigation
  };

  const handleMapSelection = async (mapUuid: string) => {
    if (!mapUuid) {
      setSelectedMapUuid('');
      setMapObjectives([]);
      return;
    }

    setIsLoadingMap(true);
    try {
      // Fetch the selected map
      const selectedMap = await dispatch(fetchMilestoneMap(mapUuid)).unwrap();

      // Fetch objectives for the selected map
      if (selectedMap.objectiveUuids.length > 0) {
        const objectives = await dispatch(fetchObjectives(selectedMap.objectiveUuids)).unwrap();
        // Filter out objectives that are already on the current map
        const availableObjectives = objectives.filter(
          (obj) => !milestoneMap?.objectiveUuids.includes(obj.uuid),
        );
        setMapObjectives(availableObjectives);
      }
    } catch (error) {
      console.error('Failed to load map and objectives:', error);
    } finally {
      setIsLoadingMap(false);
    }
  };

  const handleObjectiveSelection = (objective: Objective1) => {
    if (!milestoneMap) return;

    try {
      // Insert the selected objective UUID into the correct position
      const updatedObjectiveUuids = [...milestoneMap.objectiveUuids];
      updatedObjectiveUuids.splice(row, 0, objective.uuid);

      // Create updates object with base properties
      const updates: any = {
        objectiveUuids: updatedObjectiveUuids,
      };

      // If this is a parent objective, also update parentObjectiveUuids
      if (editingType === 'parent') {
        const updatedParentUuids = [...(milestoneMap.parentObjectiveUuids || [])];
        updatedParentUuids.push(objective.uuid);
        updates.parentObjectiveUuids = updatedParentUuids;
      }

      // Update the milestone map with the new objective and parent designation
      dispatch(
        updateMilestoneMap({
          uuid: milestoneMapUuid,
          updates,
        }),
      );

      // Calculate and apply parent/child relationship updates based on the new structure
      const allObjectivesIncludingAdded = [...allObjectives, objective];
      const updatedMilestoneMap = { ...milestoneMap, ...updates };
      const relationshipUpdates = calculateObjectiveRelationshipUpdates(
        updatedMilestoneMap,
        allObjectivesIncludingAdded,
      );

      // Apply the calculated updates to each objective that needs changes
      Object.entries(relationshipUpdates).forEach(([objectiveUuid, updates]) => {
        dispatch(updateObjective({ uuid: objectiveUuid, updates }));
      });

      // Remove the placeholder row
      dispatch(removePlaceholderRow({ row }));
    } catch (error) {
      console.error('Failed to add existing objective:', error);
    }

    // Reset state
    setIsEditing(false);
    setEditingType(null);
    setSelectedMapUuid('');
    setMapObjectives([]);
  };

  const resetUI = () => {
    setIsEditing(false);
    setEditingType(null);
    setSelectedMapUuid('');
    setMapObjectives([]);
  };

  if (isEditing) {
    return (
      <div
        className="flex w-full gap-2"
        onBlur={(e) => {
          // Only reset if we're not focusing on either select list
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            resetUI();
          }
        }}
      >
        <select
          className="w-1/2 px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white shadow-lg"
          value={selectedMapUuid}
          onChange={(e) => {
            setSelectedMapUuid(e.target.value);
            handleMapSelection(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              resetUI();
            }
          }}
          autoFocus
        >
          <option value="">Select milestone map...</option>
          {accessibleMaps
            // Don't show the current map as a selectable option.
            .filter((map) => map.uuid !== milestoneMapUuid)
            .map((map: { uuid: string; title: string }) => (
              <option key={map.uuid} value={map.uuid}>
                {map.title}
              </option>
            ))}
        </select>
        <select
          className="w-1/2 px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white shadow-lg"
          onChange={(e) => {
            const selectedObjective = mapObjectives.find(
              (obj: Objective1) => obj.uuid === e.target.value,
            );
            if (selectedObjective) {
              handleObjectiveSelection(selectedObjective);
            }
          }}
          disabled={!selectedMapUuid || isLoadingMap}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              resetUI();
            }
          }}
        >
          <option value="">Select objective...</option>
          {isLoadingMap ? (
            <option value="" disabled>
              Loading objectives...
            </option>
          ) : (
            mapObjectives.map((obj: Objective1) => (
              <option key={obj.uuid} value={obj.uuid}>
                {obj.text}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  return (
    <div className="flex w-full">
      <div
        className="w-8 px-2 py-1 text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700 flex items-center justify-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
        onClick={() => {
          setIsEditing(true);
          setEditingType('parent');
        }}
        onKeyDown={(e) => handleKeyDown(e, 'parent')}
        role="button"
        aria-label="Select existing objective to add as parent"
        data-testid="add-parent-button"
        tabIndex={0}
      >
        <span className="text-lg">+</span>
      </div>
      <div
        className="flex-1 px-2 py-1 text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        onClick={() => {
          setIsEditing(true);
          setEditingType('child');
        }}
        onKeyDown={(e) => handleKeyDown(e, 'child')}
        role="button"
        aria-label="Select existing objective to add as child"
        data-testid="add-child-button"
        tabIndex={internalFocus === 'child' ? 0 : -1}
      >
        Add from another milestone map
      </div>
    </div>
  );
}

export function ObjectivePlaceholderInput(props: ObjectivePlaceholderInputProps) {
  const enableAddExistingObjectives = useSelector(
    (state: RootState) => state.map3UI.mapControls.enableAddExistingObjectives,
  );

  return enableAddExistingObjectives ? (
    <AddExistingObjectiveInput {...props} />
  ) : (
    <AddNewObjectiveInput {...props} />
  );
}
